import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import PrescriptionFormSheet from '@/components/clinic/PrescriptionFormSheet';
import { useAuth } from '@/contexts/AuthContext';
import { useClinic } from '@/contexts/ClinicContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  User,
  CreditCard,
  Calendar,
  ExternalLink,
  Loader2,
  ClipboardList,
  Stethoscope,
  Pill,
  Building2,
  FileCheck,
  Ban,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CircleDot,
  FlaskConical,
  Mail,
  Truck,
  X,
} from 'lucide-react';
import { useGuidedOrder, useCancelGuidedOrder, useResendAmendmentConsent } from '@/hooks/useGuidedOrders';
import { canPrescribeAgainstGuidedOrder } from '@/lib/guidedOrderEligibility';
import { resendIntakeEmail } from '@/services/intakeService';
import { toast } from '@/hooks/use-toast';
import { OrderLifecycleTracker } from './OrderLifecycleTracker';
import { GuidedOrderStatusBadge } from './GuidedOrderStatusBadge';
import { GuidedOrderReferrerSection } from './GuidedOrderReferrerSection';
import type { GuidedOrder } from '@/types/guidedOrder';

export interface GuidedOrderDetailSheetProps {
  orderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewPrescription?: (prescriptionId: string) => void;
  onViewLabOrder?: (labOrderId: string) => void;
}

const TERMINAL_STATUSES = ['fulfilled', 'cancelled', 'expired'];

function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function formatDate(ts: string | null): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString();
}

function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function patientFullName(patient: { first_name: string; middle_name?: string; last_name: string }): string {
  return [patient.first_name, patient.middle_name, patient.last_name].filter(Boolean).join(' ');
}

function formatAmount(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

function holdExpiryLabel(holdExpiresAt: string | null): { text: string; urgent: boolean } {
  if (!holdExpiresAt) return { text: '—', urgent: false };
  const d = new Date(holdExpiresAt);
  const now = new Date();
  const days = Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return { text: 'Expired', urgent: true };
  if (days === 0) return { text: 'Expires today', urgent: true };
  if (days <= 2) return { text: `${days} day${days > 1 ? 's' : ''} remaining`, urgent: true };
  return { text: `${days} days remaining`, urgent: false };
}

function StatusDot({ color }: { color: 'green' | 'yellow' | 'red' | 'blue' | 'slate' }) {
  const cls: Record<string, string> = {
    green: 'bg-green-500',
    yellow: 'bg-amber-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
    slate: 'bg-slate-400',
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${cls[color]}`} />;
}

function statusColor(status: string | undefined): 'green' | 'yellow' | 'red' | 'blue' | 'slate' {
  if (!status) return 'slate';
  const s = status.toLowerCase();
  if (['completed', 'active', 'verified', 'captured', 'fulfilled'].some(k => s.includes(k))) return 'green';
  if (['pending', 'submitted', 'in_review', 'processing', 'hold'].some(k => s.includes(k))) return 'blue';
  if (['failed', 'cancelled', 'expired', 'rejected'].some(k => s.includes(k))) return 'red';
  if (['awaiting', 'draft'].some(k => s.includes(k))) return 'yellow';
  return 'slate';
}

function DetailRow({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex justify-between items-start gap-4 py-1.5">
      <span className="text-sm text-slate-500 shrink-0">{label}</span>
      <span className={`text-sm text-slate-900 text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
    </div>
  );
}

function SectionCard({
  icon: Icon,
  title,
  children,
  badge,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
  badge?: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          {title}
          {badge && <span className="ml-auto">{badge}</span>}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function PrescriptionRow({
  label,
  rx,
  onView,
}: {
  label: string;
  rx: { id: string; status: string; created_at: string } | undefined | null;
  onView?: (id: string) => void;
}) {
  if (!rx) return null;
  return (
    <div className="rounded-md border p-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
        <div className="flex items-center gap-1.5">
          <StatusDot color={statusColor(rx.status)} />
          <span className="text-xs font-medium capitalize">{rx.status}</span>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-slate-500 truncate max-w-[260px]">{rx.id}</span>
        {onView && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => onView(rx.id)}>
            View <ExternalLink className="ml-1 h-3 w-3" />
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-400">Created {formatDate(rx.created_at)}</p>
    </div>
  );
}

export function GuidedOrderDetailSheet({
  orderId,
  open,
  onOpenChange,
  onViewPrescription,
  onViewLabOrder,
}: GuidedOrderDetailSheetProps) {
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRequestSubmitted, setCancelRequestSubmitted] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  // Intake resend state — scoped to the Enrollment card so a clinic
  // user can re-send the intake email to a patient who hasn't
  // completed it yet. Separate from the amendment-consent resend
  // above so the two buttons don't share a spinner.
  const [resendingIntake, setResendingIntake] = useState(false);
  const [intakeResendSuccess, setIntakeResendSuccess] = useState(false);
  const [prescribeOpen, setPrescribeOpen] = useState(false);
  const { user } = useAuth();
  const { clinic } = useClinic();
  const { data, isLoading, error, refetch } = useGuidedOrder(open && orderId ? orderId : undefined);
  const cancelMutation = useCancelGuidedOrder();
  const resendMutation = useResendAmendmentConsent();

  const order = data?.data;
  const hasPendingCancellation = order?.cancellationRequests?.some((cr: any) => cr.status === 'pending');
  const activePrescription = order?.verifiedPrescription || order?.mdiPrescription || order?.checkoutPrescription;

  // Nimbus prescribe-against-order CTA visibility — eligibility is shared
  // with the GuidedOrderPickerDialog via canPrescribeAgainstGuidedOrder so
  // both entry points stay in lockstep.
  const myPractitionerId = (user as any)?.practitioner?.id
    || (user as any)?.practitionerId
    || (clinic as any)?.practitioner?.id
    || null;
  const canPrescribeAgainstNimbusOrder = canPrescribeAgainstGuidedOrder(order, myPractitionerId);
  const isPrescriptionShipped = activePrescription && ['shipped', 'delivered'].includes(activePrescription.status);
  const canCancel =
    order &&
    !TERMINAL_STATUSES.includes(order.status) &&
    !isPrescriptionShipped &&
    !hasPendingCancellation &&
    !cancelRequestSubmitted &&
    cancelMutation.isPending === false;

  const handleCancel = () => {
    if (!orderId || !cancelReason.trim()) return;
    setCancelConfirmOpen(true);
  };

  const confirmCancel = () => {
    if (!orderId || !cancelReason.trim()) return;
    setCancelConfirmOpen(false);
    cancelMutation.mutate(
      { orderId, reason: cancelReason.trim() },
      {
        onSuccess: () => {
          setCancelReason('');
          setCancelRequestSubmitted(true);
        },
      }
    );
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setCancelReason('');
      setCancelRequestSubmitted(false);
      setCancelConfirmOpen(false);
    }
    onOpenChange(next);
  };

  if (!open) return null;

  const meta = order?.metadata as Record<string, unknown> | undefined;
  const holdExpiry = order ? holdExpiryLabel(order.hold_expires_at) : null;
  const hasPrescriptions = order?.checkoutPrescription || order?.mdiPrescription || order?.verifiedPrescription;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[600px] sm:max-w-[600px] overflow-y-auto p-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 p-4 text-sm text-red-800">
            Failed to load order. Please try again.
          </div>
        )}
        {order && (
          <>
            <SheetHeader className="flex-row items-start justify-between gap-4 space-y-0">
              <SheetTitle className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-semibold text-slate-900">Guided Order</span>
                    {order.order_number != null && (
                      <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 font-mono text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-200">
                        GO-{order.order_number}
                      </span>
                    )}
                  </div>
                  {order.order_number == null && (
                    <p className="text-xs font-mono text-slate-400 truncate">{order.id}</p>
                  )}
                </div>
                {order.cancellationRequests?.some((cr: any) => cr.status === 'pending') ? (
                  <Badge className="ml-auto shrink-0 bg-red-100 text-red-700 border-red-200 hover:bg-red-100">
                    <Ban className="h-3 w-3 mr-1" />
                    Cancellation Requested
                  </Badge>
                ) : (
                  <GuidedOrderStatusBadge status={order.status} className="ml-auto shrink-0" />
                )}
              </SheetTitle>
              <SheetClose asChild>
                <button className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 hover:bg-slate-50 transition-colors">
                  <X className="h-4 w-4 text-slate-600" />
                </button>
              </SheetClose>
            </SheetHeader>

            <div className="mt-6 space-y-4">
              {/* ── Nimbus "Create Order" CTA ── */}
              {canPrescribeAgainstNimbusOrder && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-900">
                      Review complete — ready to prescribe
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      Submit the prescription to finalize this order. The card on file will be charged automatically.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setPrescribeOpen(true)}
                    className="shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Create Order
                  </Button>
                </div>
              )}

              {/* ── Cancellation Request Banner ── */}
              {order.cancellationRequests?.some((cr: any) => cr.status === 'pending') && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Cancellation request pending</p>
                    <p className="text-xs text-red-600 mt-0.5">
                      Reason: {order.cancellationRequests.find((cr: any) => cr.status === 'pending')?.reason}
                    </p>
                    <p className="text-xs text-red-500 mt-0.5">Awaiting super admin approval</p>
                  </div>
                </div>
              )}

              {/* ── Lifecycle tracker ── */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">Lifecycle</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <OrderLifecycleTracker
                    status={order.status}
                    paymentStatus={order.payment_status}
                    holdExpiresAt={order.hold_expires_at}
                  />
                </CardContent>
              </Card>

              {/* ── Patient & clinic ── */}
              <SectionCard icon={User} title="Patient">
                {order.patient ? (
                  <>
                    <DetailRow
                      label="Name"
                      value={
                        <span className="font-medium">
                          {patientFullName(order.patient)}
                        </span>
                      }
                    />
                    {order.patient.email && (
                      <DetailRow label="Email" value={order.patient.email} />
                    )}
                  </>
                ) : (
                  <DetailRow label="Patient ID" value={order.patient_id} mono />
                )}
                {order.clinic && (
                  <>
                    <Separator className="my-2" />
                    <DetailRow
                      label="Clinic"
                      value={
                        <span className="flex items-center gap-1.5">
                          <Building2 className="h-3 w-3 text-slate-400" />
                          {order.clinic.name}
                        </span>
                      }
                    />
                    <DetailRow label="Archetype" value={order.clinic.clinic_archetype} />
                  </>
                )}
                {meta?.program_label && (
                  <DetailRow label="Program" value={meta.program_label as string} />
                )}
                <DetailRow label="Gate mode" value={order.gate_mode} />
              </SectionCard>

              {/* ── Referrer ── */}
              <GuidedOrderReferrerSection order={order} />

              {/* ── Enrollment ── */}
              <SectionCard
                icon={ClipboardList}
                title="Enrollment"
                badge={
                  order.pendingIntake ? (
                    <Badge
                      variant="outline"
                      className={
                        order.pendingIntake.status === 'completed'
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : 'bg-amber-50 text-amber-700 border-amber-300'
                      }
                    >
                      {order.pendingIntake.status === 'completed' ? (
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                      ) : (
                        <Clock className="h-3 w-3 mr-1" />
                      )}
                      {order.pendingIntake.status}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                      Not started
                    </Badge>
                  )
                }
              >
                {order.pendingIntake ? (
                  <>
                    <DetailRow label="Enrollment ID" value={order.pendingIntake.id} mono />
                    <DetailRow label="Status" value={
                      <span className="flex items-center gap-1.5">
                        <StatusDot color={statusColor(order.pendingIntake.status)} />
                        <span className="capitalize">{order.pendingIntake.status}</span>
                      </span>
                    } />
                    <DetailRow
                      label="Completed"
                      value={order.pendingIntake.completed_at ? formatTimestamp(order.pendingIntake.completed_at) : 'Pending'}
                    />

                    {/* Resend intake email — only while the intake is
                        still outstanding. Completed / expired intakes
                        don't need another email. Mirrors the
                        resend-consent affordance in PatientIntakesTab
                        so the behaviour is consistent. */}
                    {order.pendingIntake.status !== 'completed' && (
                      <div className="pt-2 flex items-center gap-3">
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-slate-200"
                          disabled={resendingIntake}
                          onClick={async () => {
                            if (!order.pendingIntake) return;
                            setResendingIntake(true);
                            setIntakeResendSuccess(false);
                            try {
                              const result = await resendIntakeEmail(order.pendingIntake.id);
                              if (result.success) {
                                setIntakeResendSuccess(true);
                                toast({
                                  title: 'Intake email resent',
                                  description: 'The patient has been notified to complete their intake.',
                                });
                              } else {
                                toast({
                                  title: 'Failed to resend',
                                  description: result.message || 'Could not resend the intake email.',
                                  variant: 'destructive',
                                });
                              }
                            } catch (err) {
                              toast({
                                title: 'Failed to resend',
                                description: err instanceof Error ? err.message : 'An error occurred while resending.',
                                variant: 'destructive',
                              });
                            } finally {
                              setResendingIntake(false);
                            }
                          }}
                        >
                          {resendingIntake ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Resending…</>
                          ) : (
                            <><Mail className="h-3.5 w-3.5 mr-1.5" /> Resend Intake Email</>
                          )}
                        </Button>
                        {intakeResendSuccess && (
                          <span className="text-xs text-emerald-600">Sent ✓</span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400">No enrollment form has been sent yet.</p>
                )}
              </SectionCard>

              {/* ── MDI case ── */}
              {(order as any).fulfillment_source !== 'nimbus' && (
              <SectionCard
                icon={Stethoscope}
                title="MDI Provider Review"
                badge={
                  order.mdiCase ? (
                    <Badge
                      variant="outline"
                      className={
                        order.mdiCase.status === 'completed'
                          ? 'bg-green-50 text-green-700 border-green-300'
                          : order.mdiCase.status === 'cancelled' || order.mdiCase.status === 'failed'
                            ? 'bg-red-50 text-red-700 border-red-300'
                            : 'bg-purple-50 text-purple-700 border-purple-300'
                      }
                    >
                      <CircleDot className="h-3 w-3 mr-1" />
                      {order.mdiCase.status}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                      Not submitted
                    </Badge>
                  )
                }
              >
                {order.mdiCase ? (
                  <>
                    <DetailRow label="MDI Case ID" value={order.mdiCase.mdi_case_id} mono />
                    <DetailRow label="Internal ID" value={order.mdiCase.id} mono />
                    <DetailRow label="Status" value={
                      <span className="flex items-center gap-1.5">
                        <StatusDot color={statusColor(order.mdiCase.status)} />
                        <span className="capitalize">{order.mdiCase.status}</span>
                      </span>
                    } />
                  </>
                ) : (
                  <p className="text-sm text-slate-400">
                    Case has not been submitted to MDI for provider review.
                  </p>
                )}
              </SectionCard>
              )}

              {/* ── Prescriptions ── */}
              <SectionCard
                icon={Pill}
                title="Orders"
                badge={
                  order.verifiedPrescription ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      <FileCheck className="h-3 w-3 mr-1" />
                      Verified
                    </Badge>
                  ) : hasPrescriptions ? (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending verification
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                      None
                    </Badge>
                  )
                }
              >
                {hasPrescriptions ? (
                  <div className="space-y-2">
                    <PrescriptionRow
                      label="Checkout Rx"
                      rx={order.checkoutPrescription}
                      onView={onViewPrescription}
                    />
                    <PrescriptionRow
                      label="MDI Rx"
                      rx={order.mdiPrescription}
                      onView={onViewPrescription}
                    />
                    <PrescriptionRow
                      label="Verified Rx"
                      rx={order.verifiedPrescription}
                      onView={onViewPrescription}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No prescriptions linked to this order yet.</p>
                )}
              </SectionCard>

              {/* ── Lab orders ── */}
              {order.labOrders && order.labOrders.length > 0 && (
                <SectionCard
                  icon={FlaskConical}
                  title="Lab orders"
                  badge={
                    <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                      {order.labOrders.length}
                    </Badge>
                  }
                >
                  <div className="space-y-2">
                    {order.labOrders.map((lab) => (
                      <div
                        key={lab.id}
                        className="rounded-md border p-3 space-y-1.5 flex items-center justify-between"
                      >
                        <div>
                          <div className="flex items-center gap-1.5">
                            <StatusDot color={statusColor(lab.status)} />
                            <span className="text-xs font-medium capitalize">{lab.status}</span>
                          </div>
                          <p className="text-xs font-mono text-slate-500 truncate max-w-[260px] mt-0.5">{lab.id}</p>
                          <p className="text-xs text-slate-400">Created {formatDate(lab.created_at)}</p>
                        </div>
                        {onViewLabOrder && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-xs"
                            onClick={() => onViewLabOrder(lab.id)}
                          >
                            View <ExternalLink className="ml-1 h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* ── Shipping ── */}
              {(order.shipping_carrier_id || meta?.shipping) && (() => {
                const s = (meta?.shipping as Record<string, unknown>) || {};
                const carrierName = (s.carrier_name as string) || order.shipping_carrier_id || '—';
                const serviceName = (s.service_name as string) || null;
                const costCents = Number(s.cost_cents) || 0;
                const originalCostCents = Number(s.original_cost_cents) || 0;
                const isFree = !!s.is_free_shipment;
                return (
                  <SectionCard icon={Truck} title="Shipping">
                    <DetailRow label="Carrier" value={carrierName} />
                    {serviceName && <DetailRow label="Service" value={serviceName} />}
                    <DetailRow
                      label="Cost"
                      value={
                        isFree ? (
                          <span className="flex items-center gap-1.5">
                            {originalCostCents > 0 && (
                              <span className="line-through text-muted-foreground">{formatCents(originalCostCents)}</span>
                            )}
                            <span className="text-green-600 font-medium">FREE</span>
                          </span>
                        ) : formatCents(costCents)
                      }
                    />
                  </SectionCard>
                );
              })()}

              {/* ── Payment ── */}
              <SectionCard
                icon={CreditCard}
                title="Payment"
                badge={
                  order.payment_status === 'captured' ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Captured
                    </Badge>
                  ) : order.payment_status === 'capture_failed' ? (
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Failed
                    </Badge>
                  ) : order.payment_status === 'cancelled' ? (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                      <Ban className="h-3 w-3 mr-1" />
                      Released
                    </Badge>
                  ) : order.payment_status === 'hold_placed' ? (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                      <Clock className="h-3 w-3 mr-1" />
                      Held
                    </Badge>
                  ) : order.payment_status === 'pending_amendment' ? (
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Pending Amendment
                    </Badge>
                  ) : order.payment_status === 'deferred' ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                      <Clock className="h-3 w-3 mr-1" />
                      Deferred
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Pending
                    </Badge>
                  )
                }
              >
                <DetailRow label="Amount" value={<span className="font-semibold text-base">{formatAmount(order.amount_cents)}</span>} />
                {order.application_fee_cents > 0 && (
                  <DetailRow label="Platform fee" value={formatAmount(order.application_fee_cents)} />
                )}

                <Separator className="my-2" />

                {meta?.card_brand && (
                  <DetailRow
                    label="Card"
                    value={
                      <span className="capitalize">
                        {meta.card_brand as string} •••• {(meta.card_last4 as string) || '****'}
                      </span>
                    }
                  />
                )}

                {order.hold_placed_at && (
                  <DetailRow label="Hold placed" value={formatTimestamp(order.hold_placed_at)} />
                )}
                {holdExpiry && order.hold_expires_at && order.payment_status === 'hold_placed' && (
                  <DetailRow
                    label="Hold expiry"
                    value={
                      <span className={holdExpiry.urgent ? 'text-amber-600 font-medium' : ''}>
                        {holdExpiry.text}
                      </span>
                    }
                  />
                )}
                {order.captured_at && (
                  <DetailRow label="Captured at" value={formatTimestamp(order.captured_at)} />
                )}

                {order.transaction && (
                  <>
                    <Separator className="my-2" />
                    <DetailRow label="Transaction ID" value={order.transaction.id} mono />
                    <DetailRow label="Transaction status" value={
                      <span className="flex items-center gap-1.5">
                        <StatusDot color={statusColor(order.transaction.status)} />
                        <span className="capitalize">{order.transaction.status}</span>
                      </span>
                    } />
                    {order.stripe_payment_intent_id && (
                      <DetailRow label="Stripe PI" value={order.stripe_payment_intent_id} mono />
                    )}
                  </>
                )}
              </SectionCard>

              {/* ── Amendment Details ── */}
              {order.amendments && order.amendments.length > 0 && (
                <Card className="border-orange-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-orange-700">
                      <AlertTriangle className="h-4 w-4" />
                      Price Amendment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    {order.amendments.map(amendment => (
                      <div key={amendment.id} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={
                            amendment.status === 'pending' ? 'bg-orange-50 text-orange-700 border-orange-300' :
                            amendment.status === 'accepted' || amendment.status === 'auto_accepted' ? 'bg-green-50 text-green-700 border-green-300' :
                            'bg-red-50 text-red-700 border-red-300'
                          }>
                            {amendment.status === 'pending' ? 'Awaiting Consent' :
                             amendment.status === 'accepted' ? 'Accepted' :
                             amendment.status === 'auto_accepted' ? 'Auto-Accepted' :
                             amendment.status === 'declined' ? 'Declined' :
                             amendment.status === 'cancelled_by_admin' ? 'Cancelled' : amendment.status}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {formatTimestamp(amendment.created_at)}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground block">Original</span>
                            <span className="font-medium">{formatCents(amendment.original_amount_cents)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block">Updated</span>
                            <span className="font-medium">{formatCents(amendment.prescription_amount_cents)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground block">Price Increase</span>
                            <span className="font-medium text-orange-700">
                              +{formatCents(amendment.delta_cents)} ({Number(amendment.pct_change || 0).toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        {amendment.resolution_reason && (
                          <p className="text-xs text-muted-foreground">
                            Reason: {amendment.resolution_reason}
                          </p>
                        )}
                        {amendment.resolved_at && (
                          <p className="text-xs text-muted-foreground">
                            Resolved: {formatTimestamp(amendment.resolved_at)}
                          </p>
                        )}
                      </div>
                    ))}

                    {/* Resend button — only for pending amendments */}
                    {order.payment_status === 'pending_amendment' &&
                     order.amendments?.some(a => a.status === 'pending') && (
                      <>
                        <Separator className="my-2" />
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-orange-300 text-orange-700 hover:bg-orange-50"
                          disabled={resendMutation.isPending}
                          onClick={() => {
                            setResendSuccess(false);
                            resendMutation.mutate(order.id, {
                              onSuccess: () => setResendSuccess(true),
                            });
                          }}
                        >
                          {resendMutation.isPending ? (
                            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Sending...</>
                          ) : (
                            <><Mail className="h-3.5 w-3.5 mr-1.5" /> Resend Consent Email</>
                          )}
                        </Button>
                        {resendSuccess && (
                          <p className="text-xs text-green-600 flex items-center gap-1">
                            <CheckCircle2 className="h-3 w-3" /> Consent email resent successfully
                          </p>
                        )}
                        {resendMutation.isError && (
                          <p className="text-xs text-red-600">
                            Failed to resend. Please try again.
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* ── Timeline ── */}
              <SectionCard icon={Calendar} title="Timeline">
                <div className="relative pl-4 border-l-2 border-slate-200 space-y-3">
                  <TimelineEvent label="Created" ts={order.created_at} />
                  <TimelineEvent label="Hold placed" ts={order.hold_placed_at} />
                  {order.pendingIntake?.completed_at && (
                    <TimelineEvent label="Enrollment completed" ts={order.pendingIntake.completed_at} />
                  )}
                  {order.mdiCase && (
                    <TimelineEvent label="MDI review started" ts={order.created_at} />
                  )}
                  {order.verifiedPrescription && (
                    <TimelineEvent label="Order verified" ts={order.verifiedPrescription.created_at} />
                  )}
                  <TimelineEvent label="Payment captured" ts={order.captured_at} />
                  {order.amendments?.map(a => (
                    <span key={a.id}>
                      <TimelineEvent
                        label={`Amendment created (+${formatCents(a.delta_cents)})`}
                        ts={a.created_at}
                      />
                      {a.resolved_at && (
                        <TimelineEvent
                          label={`Amendment ${a.status}`}
                          ts={a.resolved_at}
                          variant={a.status === 'accepted' || a.status === 'auto_accepted' ? undefined : 'destructive'}
                        />
                      )}
                    </span>
                  ))}
                  {order.cancellationRequests?.map((cr: any) => (
                    <span key={cr.id}>
                      <TimelineEvent
                        label={`Cancellation requested — ${cr.reason}`}
                        ts={cr.requested_at}
                        variant="destructive"
                      />
                      {cr.status === 'approved' && cr.reviewed_at && (
                        <TimelineEvent label="Cancellation approved" ts={cr.reviewed_at} variant="destructive" />
                      )}
                      {cr.status === 'rejected' && cr.reviewed_at && (
                        <TimelineEvent
                          label={`Cancellation rejected${cr.rejection_reason ? ` — ${cr.rejection_reason}` : ''}`}
                          ts={cr.reviewed_at}
                        />
                      )}
                    </span>
                  ))}
                  {order.cancelled_at && (
                    <TimelineEvent
                      label={`Cancelled${order.cancel_reason ? ` — ${order.cancel_reason}` : ''}`}
                      ts={order.cancelled_at}
                      variant="destructive"
                    />
                  )}
                </div>
              </SectionCard>

              {/* ── Cancel ── */}
              {cancelRequestSubmitted && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="py-4">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-4 w-4" />
                      <p className="text-sm font-medium">Cancellation request submitted</p>
                    </div>
                    <p className="text-xs text-green-600 mt-1 ml-6">Awaiting super admin approval.</p>
                  </CardContent>
                </Card>
              )}
              {canCancel && (
                <Card className="border-red-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2 text-red-700">
                      <Ban className="h-4 w-4" />
                      Request order cancellation
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    {order?.subscription_id && (
                      <p className="text-xs text-amber-600">
                        This is a recurring order. Cancellation will also stop the subscription and cancel any unfulfilled refill prescriptions.
                      </p>
                    )}
                    <div>
                      <Label htmlFor="cancel-reason" className="text-xs">
                        Reason (required)
                      </Label>
                      <Input
                        id="cancel-reason"
                        value={cancelReason}
                        onChange={e => setCancelReason(e.target.value)}
                        placeholder="e.g. Patient requested cancellation"
                        className="mt-1"
                      />
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={!cancelReason.trim() || cancelMutation.isPending}
                      onClick={handleCancel}
                    >
                      {cancelMutation.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-1" />
                      ) : (
                        <Ban className="h-4 w-4 mr-1" />
                      )}
                      Request cancellation
                    </Button>
                    {cancelMutation.isError && (
                      <p className="text-xs text-red-600">
                        {(cancelMutation.error as Error)?.message || 'Failed to submit cancellation request'}
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </>
        )}
      </SheetContent>

      {/* Cancellation Confirmation Dialog */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm cancellation request</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to request cancellation of this guided order? This will be sent to the super admin for approval.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
            <p className="font-medium text-xs text-slate-500 mb-1">Reason:</p>
            <p>{cancelReason}</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {cancelMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Ban className="h-4 w-4 mr-1" />
              )}
              Submit request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nimbus prescribe-against-order form */}
      {order && myPractitionerId && (
        <PrescriptionFormSheet
          open={prescribeOpen}
          onOpenChange={setPrescribeOpen}
          onSubmit={() => { refetch?.(); }}
          mode="create"
          patientId={(order as any).patient_id || ''}
          practitionerId={myPractitionerId}
          guidedOrderId={order.id}
        />
      )}
    </Sheet>
  );
}

function TimelineEvent({
  label,
  ts,
  variant,
}: {
  label: string;
  ts: string | null;
  variant?: 'destructive';
}) {
  if (!ts) return null;
  return (
    <div className="relative">
      <div className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-slate-400" />
      <p className={`text-sm ${variant === 'destructive' ? 'text-red-600' : 'text-slate-700'}`}>
        {label}
      </p>
      <p className="text-xs text-slate-400">{formatTimestamp(ts)}</p>
    </div>
  );
}
